import { Icon } from '@ps/isp-web-component-library';
import { CloseMedium } from '@ps/isp-web-component-library/icons/CloseMedium';
import { Search as SearchIcon } from '@ps/isp-web-component-library/icons/Search';
import type { TranslationTag } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation } from '@ps/web-shared-components';
import { uniqueId } from 'lodash';
import React, { useRef } from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';

import { TXTCLI_Clear, TXTCLI_Search } from './Search.messages.json';
import styles from './Search.scss';

interface Props {
  searchTerm: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onClear: () => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  placeholderTranslation: TranslationTag;
  trackingLabel: string | undefined;
}

export const Search: React.FC<Props> = ({
  placeholderTranslation,
  searchTerm,
  onChange,
  onClear,
  onFocus,
  onBlur,
  trackingLabel
}) => {
  const { current: inputId } = useRef(uniqueId('search'));
  const { inputFocused } = useTrackers();
  const { formatMessage } = useRegulatoryIntl();

  const handleSearchOnFocus = () => {
    if (onFocus) {
      onFocus();
    }
    inputFocused({ fieldName: trackingLabel, fieldType: 'text' });
  };

  return (
    <div className={styles.search}>
      <label htmlFor={inputId} className={styles.label}>
        <Translation {...placeholderTranslation} />
      </label>
      <div className={styles.inputWrapper}>
        {searchTerm ? (
          <button type="button" onClick={onClear} className={styles.iconWrapper}>
            <Icon
              icon={CloseMedium}
              className={styles.clearIcon}
              label={formatMessage(TXTCLI_Clear)}
              size="sm"
            />
          </button>
        ) : (
          <div className={styles.iconWrapper}>
            <Icon
              icon={SearchIcon}
              className={styles.searchIcon}
              label={formatMessage(TXTCLI_Search)}
              size="sm"
            />
          </div>
        )}
        <input
          className={styles.input}
          id={inputId}
          onFocus={handleSearchOnFocus}
          onChange={onChange}
          placeholder={formatMessage(placeholderTranslation)}
          type="text"
          value={searchTerm}
          onBlur={onBlur}
          title={formatMessage(TXTCLI_Search)}
          autoComplete="off"
        />
      </div>
    </div>
  );
};
